import React, { useMemo } from 'react';
import DOMPurify from 'dompurify';
import styles from './index.module.less';

interface IMarkdownContentProps {
  text: string;
}

const CODE_PLACEHOLDER = '%%ASSISTANT_CODE_';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const inlineMarkdown = (value: string) =>
  value
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(
      /\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
    )
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/__([^_]+)__/g, '<strong>$1</strong>');

const splitTableCells = (line: string) => {
  let inner = line.trim();
  if (inner.startsWith('|')) {
    inner = inner.slice(1);
  }
  if (inner.endsWith('|')) {
    inner = inner.slice(0, -1);
  }
  return inner.split('|').map((cell) => cell.trim());
};

const isTableRow = (line: string) => {
  const trimmed = line.trim();
  return trimmed.startsWith('|') && trimmed.includes('|', 1);
};

const isSeparatorRow = (line: string) => {
  const cells = splitTableCells(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

const markdownToSafeHtml = (source: string) => {
  const fences: string[] = [];
  const withFences = source.replace(
    /```[\w-]*\n?([\s\S]*?)```/g,
    (_match, code: string) => {
      fences.push(
        `<pre><code>${escapeHtml(code.replace(/\n$/, ''))}</code></pre>`
      );
      return `\n${CODE_PLACEHOLDER}${fences.length - 1}%%\n`;
    }
  );
  const lines = escapeHtml(withFences).split('\n');
  const html: string[] = [];
  let listType: 'ul' | 'ol' | undefined;
  let paragraph: string[] = [];
  let tableRows: string[] = [];

  const flushParagraph = () => {
    if (!paragraph.length) {
      return;
    }
    html.push(`<p>${paragraph.join('<br/>')}</p>`);
    paragraph = [];
  };

  const flushList = () => {
    if (!listType) {
      return;
    }
    html.push(listType === 'ul' ? '</ul>' : '</ol>');
    listType = undefined;
  };

  const flushTable = () => {
    if (!tableRows.length) {
      return;
    }
    if (tableRows.length >= 2 && isSeparatorRow(tableRows[1])) {
      const header = splitTableCells(tableRows[0]);
      html.push('<table><thead><tr>');
      header.forEach((cell) => {
        html.push(`<th>${inlineMarkdown(cell)}</th>`);
      });
      html.push('</tr></thead><tbody>');
      tableRows.slice(2).forEach((row) => {
        if (isSeparatorRow(row)) {
          return;
        }
        html.push('<tr>');
        splitTableCells(row).forEach((cell) => {
          html.push(`<td>${inlineMarkdown(cell)}</td>`);
        });
        html.push('</tr>');
      });
      html.push('</tbody></table>');
    } else {
      tableRows.forEach((row) => paragraph.push(inlineMarkdown(row)));
      flushParagraph();
    }
    tableRows = [];
  };

  lines.forEach((line) => {
    if (isTableRow(line)) {
      flushParagraph();
      flushList();
      tableRows.push(line);
      return;
    }
    flushTable();
    const fence = line.match(/^%%ASSISTANT_CODE_(\d+)%%$/);
    if (fence) {
      flushParagraph();
      flushList();
      html.push(fences[Number(fence[1])]);
      return;
    }
    if (/^---+$/.test(line.trim())) {
      flushParagraph();
      flushList();
      html.push('<hr/>');
      return;
    }
    const heading = line.match(/^(#{1,4})\s+(.+)$/);
    if (heading) {
      flushParagraph();
      flushList();
      const level = heading[1].length;
      html.push(`<h${level}>${inlineMarkdown(heading[2])}</h${level}>`);
      return;
    }
    const quote = line.match(/^&gt;\s?(.*)$/);
    if (quote) {
      flushParagraph();
      flushList();
      html.push(`<blockquote>${inlineMarkdown(quote[1])}</blockquote>`);
      return;
    }
    const unordered = line.match(/^[-*]\s+(.+)$/);
    if (unordered) {
      flushParagraph();
      if (listType !== 'ul') {
        flushList();
        html.push('<ul>');
        listType = 'ul';
      }
      html.push(`<li>${inlineMarkdown(unordered[1])}</li>`);
      return;
    }
    const ordered = line.match(/^\d+\.\s+(.+)$/);
    if (ordered) {
      flushParagraph();
      if (listType !== 'ol') {
        flushList();
        html.push('<ol>');
        listType = 'ol';
      }
      html.push(`<li>${inlineMarkdown(ordered[1])}</li>`);
      return;
    }
    if (!line.trim()) {
      flushParagraph();
      flushList();
      return;
    }
    flushList();
    paragraph.push(inlineMarkdown(line));
  });
  flushTable();
  flushParagraph();
  flushList();

  const sanitized = DOMPurify.sanitize(html.join(''), {
    RETURN_DOM_FRAGMENT: true,
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'ul',
      'ol',
      'li',
      'code',
      'pre',
      'h1',
      'h2',
      'h3',
      'h4',
      'a',
      'blockquote',
      'hr',
      'table',
      'thead',
      'tbody',
      'tr',
      'th',
      'td',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });

  const container = document.createElement('div');
  container.appendChild(sanitized);
  container.querySelectorAll('ol').forEach((list) => {
    // 原生序号从 1 开始，按实际条目数为每个列表单独预留空间。
    const digits = String(list.children.length).length;
    list.style.setProperty('--ordered-list-digits', String(digits));
  });

  return container.innerHTML;
};

const MarkdownContent = ({ text }: IMarkdownContentProps) => {
  const html = useMemo(() => markdownToSafeHtml(text), [text]);
  return (
    <div
      className={styles.markdown}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

export default MarkdownContent;
